'use client';

import { useEffect } from 'react';
import { type StateObject, useStateObject } from '~/utils/react';
import { I18N_CONF } from '@funkey/locales/config';
import { Icon } from '~/components/primitives';
import { Modal } from '~/components/primitives';
import { ActivitySearchModal } from './SearchModal';
import { useI18n } from '~/features/i18n';
import { useCallback } from 'react';
import { usePortalCtx } from '~/features/global/portal';
import { parseAsString, useQueryState } from 'nuqs';
import { AnimatePresence } from 'motion/react';
import { useHotkey } from '@tanstack/react-hotkeys';
import Link from 'next/link';
import { usePostHog } from 'posthog-js/react';
import { cx } from 'class-variance-authority';
import { usePlpExperiment } from '~/features/activities/search/plpExperiment';
import { PLP_SEARCH_INPUT_ID, PLP_SEARCH_TITLE_ID, plpSearchEntryUrl } from '~/features/activities/search/shared';
export const SearchModalButton: React.FC<{
  className?: string;
}> = props => {
  const i18n = useI18n();
  const portal = usePortalCtx();
  const isFinetuning = usePlpExperiment();
  const posthog = usePostHog();
  const modal = Modal.useState(false);
  const [queryParam] = useQueryState('query', parseAsString);
  const search = useStateObject(() => queryParam?.trim() ?? '');
  useEffect(() => {
    if (!modal.isOpen) {
      search.set(queryParam?.trim() ?? '');
    }
  }, [queryParam, modal.isOpen]);
  const syncSearchFromQueryParam = useCallback(() => {
    search.set(queryParam?.trim() ?? '');
  }, [queryParam]);
  const getTargetUrl = useCallback(() => {
    const q = search.value.trim();
    const basePath = portal.portal === 'BIZZ' ? `${portal.baseURLPath}/booking` : I18N_CONF.getTranslatedUrl('teambuildings', {
      locale: i18n.locale
    });
    return q ? `${basePath}?query=${encodeURIComponent(q)}` : basePath;
  }, [search.value, portal, i18n.locale]);
  const getAiSearchUrl = useCallback(() => {
    const q_0 = search.value.trim();
    const basePath_0 = I18N_CONF.getTranslatedUrl('teambuildings', {
      locale: i18n.locale
    });
    const params = new URLSearchParams();
    params.set('mode', 'ai');
    if (q_0) params.set('startPrompt', q_0);
    const qs = params.toString();
    return qs ? `${basePath_0}?${qs}` : `${basePath_0}?mode=ai`;
  }, [search.value, i18n.locale]);
  const handleSubmit = useCallback(() => {
    modal.close();
    window.location.href = getTargetUrl();
  }, [getTargetUrl, modal]);
  const handleAiSearchSubmit = useCallback(() => {
    modal.close();
    window.location.href = getAiSearchUrl();
  }, [getAiSearchUrl, modal]);
  const handleClose = useCallback(() => {
    modal.close();
  }, [modal]);
  const handleOpen = useCallback(() => {
    syncSearchFromQueryParam();
    modal.open();
  }, [modal, syncSearchFromQueryParam]);

  // Cmd+K / Ctrl+K to toggle search modal
  useHotkey('Mod+K', e => {
    e.preventDefault();
    if (isFinetuning && portal.portal !== 'BIZZ') {
      window.location.href = plpSearchEntryUrl(i18n.locale);
    } else if (modal.isOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  });
  const modalProps: SearchModalProps = {
    i18n,
    searchString: search,
    onModalClose: handleClose,
    onSubmit: handleSubmit,
    onAiSearchSubmit: portal.portal === 'BIZZ' ? undefined : handleAiSearchSubmit,
    onClear: () => search.set(''),
    portalHrefs: portal.portal === 'BIZZ' ? {
      PLP: getTargetUrl()
    } : {
      PLP: getTargetUrl()
    } // prettier-ignore
  };
  if (isFinetuning && portal.portal !== 'BIZZ') {
    return <Link href={plpSearchEntryUrl(i18n.locale)} aria-label={i18n._('Search')} className={props.className} onClick={e_0 => {
      posthog.capture('plp.header_search.click');
      const input = document.getElementById(PLP_SEARCH_INPUT_ID);
      const title = document.getElementById(PLP_SEARCH_TITLE_ID);
      // Modified clicks keep the browser's new-tab/window behaviour.
      if (!input && !title || e_0.metaKey || e_0.ctrlKey || e_0.shiftKey || e_0.altKey) return;
      // Already on the PLP: open the search in place instead of navigating.
      e_0.preventDefault();
      if (input) input.focus();else title?.click();
    }}>
                <Icon name="search-1" />
            </Link>;
  }
  return <>
            <button type="button" onClick={handleOpen} aria-label={i18n._('Search')} className={cx('relative', props.className)}>
                <Icon name="search-1" />
                {!!search.value && <span aria-hidden className="absolute right-0 top-0 size-2 rounded-full bg-tertiary" />}
            </button>

            <AnimatePresence>
                {modal.isOpen && <ActivitySearchModal {...modalProps} />}
            </AnimatePresence>
        </>;
};
export type SearchModalProps = {
  i18n: ReturnType<typeof useI18n>;
  searchString: StateObject<string>;
  onModalClose: () => void;
  onSubmit: () => void;
  /** WEB: navigate to PLP with `?mode=ai&startPrompt=…` (shown when query has more than two words). */
  onAiSearchSubmit?: () => void;
  onClear: () => void;
  className?: string;

  /** Portal specific paths */
  portalHrefs: {
    PLP: string;
  };
};